import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../utils/cn.js";

type Tone = "neutral" | "teal" | "mustard" | "ink" | "danger";

const tones: Record<Tone, string> = {
  neutral: "bg-line text-ink-soft",
  teal:    "bg-teal/10 text-teal",
  mustard: "bg-mustard-soft text-mustard",
  ink:     "bg-ink text-cream",
  danger:  "bg-danger/15 text-danger",
};

export interface PillProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: Tone;
  /** Optional leading element (icon, mood chip dot). */
  leading?: ReactNode;
}

/**
 * Pill — small, rounded label.
 *
 * Used for short status tags (a mood, a theme, a count). The brand
 * bible uses pills heavily for the insight card's "what I'm noticing"
 * tags and for the library's theme cloud.
 */
export const Pill = ({ tone = "neutral", leading, className, children, ...rest }: PillProps) => (
  <span
    className={cn(
      "inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-meta font-semibold leading-tight",
      tones[tone],
      className,
    )}
    {...rest}
  >
    {leading}
    {children}
  </span>
);
